import { useCallback, useEffect, useState } from "react";
import { api, API, apiError, fmt, hhmm } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Download, Copy, RefreshCw, Radio, Eye, EyeOff, KeyRound, ShieldCheck } from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { FiniIngest } from "@/components/FiniIngest";
import { OnlinePlayers } from "@/components/OnlinePlayers";
import { PlaytimeRules } from "@/components/PlaytimeRules";
import { DiscordSetup } from "@/components/DiscordSetup";
import { DeliveryPanel } from "@/components/DeliveryPanel";

const Step = ({ n, title, children }) => (
  <div className="flex gap-5 border-t border-border py-6 first:border-t-0 first:pt-0">
    <span className="mono grid h-8 w-8 shrink-0 place-items-center border border-primary/50 text-sm text-primary">
      {n}
    </span>
    <div className="min-w-0 flex-1">
      <h4 className="font-semibold">{title}</h4>
      <div className="mt-2 text-sm text-muted-foreground">{children}</div>
    </div>
  </div>
);

const Code = ({ children }) => (
  <pre className="mono mt-3 overflow-auto border border-border bg-secondary p-4 text-xs text-foreground">
    {children}
  </pre>
);

export const ServerConnect = () => {
  const [c, setC] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showKey, setShowKey] = useState(false);
  const [testing, setTesting] = useState(false);
  const [test, setTest] = useState(null);
  const [confirmRotate, setConfirmRotate] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/connection");
      setC(data);
    } catch (e) {
      toast.error(apiError(e));
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [load]);

  const copy = (text, label) => {
    const failed = () => toast.error(`Copy blocked by the browser — select the ${label} and copy it manually`);
    const p = navigator.clipboard?.writeText(text);
    if (!p || typeof p.then !== "function") return failed();
    p.then(() => toast.success(`${label} copied`)).catch(failed);
  };

  const testKey = async () => {
    setTesting(true);
    setTest(null);
    try {
      const { data } = await api.post("/admin/server-key/test");
      setTest(data);
      data.ok ? toast.success("Server key works") : toast.error("Server key was rejected");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setTesting(false);
    }
  };

  const rotateKey = async () => {
    setConfirmRotate(false);
    try {
      await api.post("/admin/server-key/rotate");
      setTest(null);
      setShowKey(true);
      await load();
      toast.success("New key generated — paste it into server.cfg now");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const download = async () => {
    setBusy(true);
    try {
      const res = await api.get("/admin/resource-download", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "liquidrp_loyalty.zip";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Resource downloaded — keys already filled in");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  if (!c) return <p className="text-sm text-muted-foreground">Loading connection status…</p>;

  return (
    <div data-testid="connect-section">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <h2 className="text-lg md:text-lg font-semibold">Connect your FiveM server</h2>
        <div className="flex items-center gap-3">
          <Button variant="outline" className="rounded-none" onClick={load} data-testid="refresh-connection-btn">
            <RefreshCw className="mr-2 h-4 w-4" /> Refresh
          </Button>
          <Button className="rounded-none" onClick={download} disabled={busy} data-testid="download-resource-btn">
            <Download className="mr-2 h-4 w-4" /> Download resource
          </Button>
        </div>
      </div>

      <div className="mt-5 grid gap-4 md:grid-cols-4">
        <div
          className={`border p-6 ${c.connected ? "border-primary/60 bg-primary/5" : "border-border bg-card"}`}
          data-testid="connection-status"
        >
          <div className="flex items-center justify-between">
            <span className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">Status</span>
            <Radio className={`h-4 w-4 ${c.connected ? "animate-pulse text-primary" : "text-muted-foreground"}`} />
          </div>
          <div className={`mt-4 text-2xl font-semibold ${c.connected ? "text-primary" : "text-muted-foreground"}`}>
            {c.connected ? "CONNECTED" : "WAITING"}
          </div>
          <div className="mt-1 text-xs text-muted-foreground">
            {c.last_contact_at ? `Last ping ${new Date(c.last_contact_at).toLocaleString()}` : "No ping received yet"}
          </div>
        </div>
        <div className="border border-border bg-card p-6" data-testid="connection-heartbeats">
          <span className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">Heartbeats 24h</span>
          <div className="mono mt-4 text-3xl font-semibold">{fmt(c.heartbeats_24h)}</div>
          <div className="mt-1 text-xs text-muted-foreground">{hhmm(c.minutes_24h)} credited</div>
        </div>
        <div className="border border-border bg-card p-6" data-testid="connection-players">
          <span className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">Players seen 24h</span>
          <div className="mono mt-4 text-3xl font-semibold">{fmt(c.unique_players_24h)}</div>
          <div className="mt-1 text-xs text-muted-foreground">{c.linked_players} linked accounts</div>
        </div>
        <div className="border border-border bg-card p-6" data-testid="connection-unlinked">
          <span className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">Unlinked accounts</span>
          <div className="mono mt-4 text-3xl font-semibold">{fmt(c.unlinked_players)}</div>
          <div className="mt-1 text-xs text-muted-foreground">staff link them in Players</div>
        </div>
      </div>

      {c.last_bad_key_at && (
        <div className="mt-4 border border-destructive/50 bg-destructive/10 p-5 text-sm text-destructive" data-testid="bad-key-warning">
          A request with an invalid server key was rejected at {new Date(c.last_bad_key_at).toLocaleString()} —
          check that SERVER_KEY in server.lua matches the key below.        </div>
      )}

      <FiniIngest />
      <DeliveryPanel />
      <DiscordSetup />
      <PlaytimeRules />
      <OnlinePlayers />

      <div className="mt-4 grid gap-4 md:grid-cols-3">
        <div className="border border-border bg-card p-8 md:col-span-2" data-testid="setup-steps">
          <h3 className="text-lg font-semibold">Setup</h3>          <div className="mt-6">
            <Step n="1" title="Option A — Fini Apollo (nothing to install)">
              If you run FiniAC, put your Apollo API keys in <span className="mono">backend/.env</span> (see the
              Fini Apollo panel above) and we poll your live player list every 60s — no resource, no restart.
            </Step>
            <Step n="2" title="Option B — download and drop in the resource">
              Hit <span className="text-foreground">Download resource</span> above — your portal URL and server
              key are baked in. Unzip <span className="mono">liquidrp_loyalty</span> into your server's{" "}
              <span className="mono">resources/</span> folder.
            </Step>
            <Step n="3" title="Add it to server.cfg and restart">
              <Code>ensure liquidrp_loyalty</Code>
              Then restart, or run <span className="mono">refresh</span> and{" "}
              <span className="mono">ensure liquidrp_loyalty</span> in the console.
            </Step>
            <Step n="4" title="Join the server and watch this page">
              With at least one player online the status above flips to{" "}
              <span className="text-primary">CONNECTED</span> within 60 seconds and heartbeats start counting.
            </Step>
            <Step n="5" title="Link a player account">
              Linking is staff-only: open the Players tab, pick the account and paste their license /
              fivem / discord / steam id into "Game identity". You can also link anyone currently
              online from "Who's online right now" above. Unclaimed in-game identities show below.
            </Step>
            <Step n="6" title="Test a redemption">
              Player redeems anything from Rewards, then types <span className="mono text-primary">/redeem</span>{" "}
              in-game — we return their unclaimed confirmation codes with the item class, your logic
              hands out the item, and the code is consumed so it can't be used twice. Point each item
              class at your own give-item logic:
              <Code>{`-- config.lua
Config.ClassHandlers = {
  cash_clean = function(src, amount, row) -- your AddMoney call
    return true
  end,
}

-- or handle everything in your own resource
AddEventHandler('liquidrp:deliverClass', function(src, class, amount, row) end)`}</Code>
            </Step>
          </div>
        </div>

        <div className="space-y-4">
          <div className="border border-border bg-card p-6" data-testid="credentials-card">
            <h3 className="text-lg font-semibold">Credentials</h3>
            <div className="mt-4 text-xs uppercase tracking-[0.2em] text-muted-foreground">Portal URL</div>
            <div className="mono mt-1 break-all text-xs">{c.portal_url}</div>
            <Button
              variant="outline"
              className="mt-3 w-full rounded-none"
              onClick={() => copy(c.portal_url, "Portal URL")}
              data-testid="copy-url-btn"
            >
              <Copy className="mr-2 h-3.5 w-3.5" /> Copy URL
            </Button>
            <div className="mt-6 flex items-center justify-between gap-2">
              <span className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Server key</span>
              <button
                type="button"
                onClick={() => setShowKey((s) => !s)}
                data-testid="toggle-key-btn"
                className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] text-muted-foreground transition-colors hover:text-primary"
              >
                {showKey ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                {showKey ? "Hide" : "Show"}
              </button>
            </div>
            <div className="mono mt-1 break-all text-xs" data-testid="server-key-value">
              {showKey ? c.server_key : "•".repeat(Math.min(44, (c.server_key || "").length))}
            </div>
            <Button
              variant="outline"
              className="mt-3 w-full rounded-none"
              onClick={() => copy(c.server_key, "Server key")}
              data-testid="copy-key-btn"
            >
              <Copy className="mr-2 h-3.5 w-3.5" /> Copy key
            </Button>
            <div className="mt-2 grid grid-cols-2 gap-2">
              <Button
                variant="outline"
                className="rounded-none"
                onClick={testKey}
                disabled={testing}
                data-testid="test-key-btn"
              >
                <ShieldCheck className="mr-2 h-3.5 w-3.5" /> {testing ? "Testing…" : "Test key"}
              </Button>
              <Button
                variant="outline"
                className="rounded-none border-destructive/50 text-destructive hover:bg-destructive/10"
                onClick={() => setConfirmRotate(true)}
                data-testid="rotate-key-btn"
              >
                <KeyRound className="mr-2 h-3.5 w-3.5" /> Rotate key
              </Button>
            </div>
            {test && (
              <p
                className={`mt-3 text-xs ${test.ok ? "text-primary" : "text-destructive"}`}
                data-testid="key-test-result"
              >
                {test.ok
                  ? `Key accepted by ${test.domain} — ${test.players_waiting} player(s) with rewards waiting`
                  : `Rejected by ${test.domain} (HTTP ${test.status}) — ${test.detail || "no detail"}`}
              </p>
            )}
            <p className="mt-4 text-xs text-muted-foreground">
              One key for every copy of the portal, stored with your settings — the same value works
              on this site and on your live domain. Keep it server-side only:{" "}
              <span className="mono">set liquidrp_server_key "…"</span> in{" "}
              <span className="mono">server.cfg</span>.
            </p>
          </div>

          <div className="border border-border bg-card p-6" data-testid="manual-test-card">
            <h3 className="text-lg font-semibold">Manual test</h3>
            <p className="mt-2 text-xs text-muted-foreground">
              Run this from any terminal to fake one minute of playtime for a license.
            </p>
            <Code>{`curl -X POST ${API}/ingest/heartbeat \\
 -H "Content-Type: application/json" \\
 -H "X-Server-Key: ${c.server_key}" \\
 -d '{"license_id":"license:test1","minutes":1}'`}</Code>
          </div>
        </div>
      </div>

      <div className="mt-4 grid gap-4 md:grid-cols-2">
        <div className="border border-border bg-card p-6" data-testid="awaiting-link-list">
          <h3 className="text-lg font-semibold">Codes awaiting claim</h3>
          <div className="mt-4 space-y-2 text-sm">
            {c.awaiting_link.length === 0 && (
              <p className="text-muted-foreground">No pending link codes.</p>
            )}
            {c.awaiting_link.map((p) => (
              <div key={p.code} className="flex items-center justify-between border-b border-border pb-2">
                <span className="mono text-primary">{p.code}</span>
                <span className="mono truncate text-xs text-muted-foreground">
                  {p.username || "unknown"} · {p.license_id}
                </span>
              </div>
            ))}
          </div>
        </div>
        <div className="border border-border bg-card p-6" data-testid="ingest-log">
          <h3 className="text-lg font-semibold">Live ingest log</h3>
          <div className="mt-4 space-y-2 text-sm">
            {c.recent.length === 0 && (
              <p className="text-muted-foreground">No heartbeats received in the last 24 hours.</p>
            )}
            {c.recent.map((r) => (
              <div key={r.id} className="flex items-center justify-between border-b border-border pb-2">
                <span className="truncate">{r.username || r.license_id}</span>
                <span className="mono text-xs text-muted-foreground">
                  +{r.points} · {new Date(r.created_at).toLocaleTimeString()}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="mt-4 border border-border bg-card p-6" data-testid="rewards-api-card">
        <h3 className="text-lg font-semibold">Rewards API for your game server</h3>
        <p className="mt-2 text-xs text-muted-foreground">
          Your server polls this map of discord id → pending rewards, then clears a player's list
          once <span className="mono">/redeem</span> has granted them.
        </p>
        <Code>{`curl ${API}/ingest/rewards \\
 -H "X-Server-Key: ${c.server_key}"

# {"390604780147441666":[
#   {"label":"1x $5,000 Marked Bills","item_class":"markedbills",
#    "amount":1,"category":"cash","code":"A4F2K9"}]}

curl -X POST ${API}/ingest/rewards/claim \\
 -H "Content-Type: application/json" \\
 -H "X-Server-Key: ${c.server_key}" \\
 -d '{"discord_id":"390604780147441666"}'

curl ${API}/ingest/catalog -H "X-Server-Key: ${c.server_key}"

curl ${API}/ingest/accounts?unlinked_only=true \\
 -H "X-Server-Key: ${c.server_key}"

curl -X POST ${API}/ingest/identify \\
 -H "Content-Type: application/json" \\
 -H "X-Server-Key: ${c.server_key}" \\
 -d '{"discord_id":"390604780147441666",
      "license_id":"license:a1b2c3","steam_id":"steam:1100001"}'`}</Code>
        <p className="mt-3 text-xs text-muted-foreground">
          Add <span className="mono">?discord_id=</span> to the rewards GET for one player, or{" "}
          <span className="mono">?labels_only=true</span> for plain strings. The claim POST clears
          everything for that player unless you pass{" "}
          <span className="mono">{`"items": ["markedbills"]`}</span> (item class, code or label).{" "}
          <span className="mono">/ingest/catalog</span> lists every reward with its item class, and{" "}
          <span className="mono">/ingest/accounts</span> + <span className="mono">/ingest/identify</span>{" "}
          let the server link a player's game account by itself — no admin needed.
        </p>
      </div>

      <div className="mt-4 flex items-center gap-3 text-xs text-muted-foreground">
        <Badge className="rounded-none uppercase" variant="secondary">
          auto-refresh 15s
        </Badge>
        Endpoints: /api/ingest/rewards · /api/ingest/rewards/claim · /api/ingest/catalog ·
        /api/ingest/accounts · /api/ingest/identify · /api/ingest/heartbeat
      </div>

      <AlertDialog open={confirmRotate} onOpenChange={setConfirmRotate}>
        <AlertDialogContent className="rounded-none" data-testid="rotate-key-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Rotate the server key?</AlertDialogTitle>
            <AlertDialogDescription>
              The current key stops working the moment you confirm — your game server will be
              rejected until the new key is pasted into <span className="mono">server.cfg</span> and
              the server restarted. There is no overlap period.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none" data-testid="rotate-cancel-btn">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              className="rounded-none bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={rotateKey}
              data-testid="rotate-confirm-btn"
            >
              Rotate key
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
